import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormHelper } from '@app/shared/helpers/form';
import { PasswordValidator } from '@app/shared/validators/password';
import { IPasswordChange } from '@app/modules/auth/interface/password-change';
import { IsEqualWith } from '@app/shared/validators/equal-value-validator';
import { NotEqualWith } from '@app/shared/validators/not-equal-value-validator';
import {Actions, ofActionDispatched, Store} from '@ngxs/store';
import { AuthActions } from '@app/modules/auth/store/auth.actions';
import { Subscription } from 'rxjs';
import { SnackNotifier } from '@app/shared/notifier/snack-notifier';
import {UserModel} from "@app/modules/user/model/user";
import {UserState} from "@app/modules/user/store/user.state";

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  @Output() public submitEvent = new EventEmitter<IPasswordChange>();

  public form: FormGroup;

  currentUser: UserModel;

  public get oldPassword(): FormControl {
    return this.form.get('oldPassword') as FormControl;
  }

  public get newPassword(): FormControl {
    return this.form.get('newPassword') as FormControl;
  }

  public get newPasswordRepeat(): FormControl {
    return this.form.get('newPasswordRepeat') as FormControl;
  }

  private _subscription = new Subscription();

  /**
   * @param fb
   * @param actions
   * @param snack
   * @param cdRef
   */
  constructor(
    private readonly fb: FormBuilder,
    private readonly actions: Actions,
    private readonly snack: SnackNotifier,
    private readonly cdRef: ChangeDetectorRef,
    private readonly store: Store
  ) {}

  public ngOnInit(): void {
    this.createForm();
    this.subscribeOnPasswordChangeFailed();
    this.subscribeOnPasswordChangeSuccess();
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }


  public submit(): void {
    FormHelper.markAllAsDirty(this.form);

    const user : UserModel = this.store.selectSnapshot(UserState.model);

    if (this.form.valid) {
      const model: IPasswordChange = {
        oldPassword: this.oldPassword.value,
        newPassword: this.newPassword.value,
        email: user.email
      };

      this.submitEvent.emit(model);
    }
  }

  public getErrorMessage(key: string, control: FormControl): string {
    return FormHelper.getErrorMessage(key, control);
  }

  private subscribeOnPasswordChangeFailed(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(AuthActions.PasswordChangeFailed))
        .subscribe(({ error }) => {
          this.oldPassword.setErrors(error);
          this.oldPassword.markAsTouched();
          this.cdRef.detectChanges();
        }),
    );
  }

  private subscribeOnPasswordChangeSuccess(): void {
    this._subscription.add(
      this.actions
        .pipe(ofActionDispatched(AuthActions.PasswordChangeSuccess))
        .subscribe(() => {
          this.snack.success('Your password has been updated!');
          this.form.reset();
        }),
    );
  }

  private createForm(): void {
    this.form = this.fb.group({
      oldPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        PasswordValidator
      ]]
    });

    this.form.addControl('newPassword', new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      PasswordValidator.validate,
      NotEqualWith.validate(this.oldPassword)
    ]));
    this.oldPassword.valueChanges.subscribe(() => this.newPassword.updateValueAndValidity());

    this.form.addControl('newPasswordRepeat', new FormControl('', [
      Validators.required,
      IsEqualWith.validate(this.newPassword)
    ]));
    this.newPassword.valueChanges.subscribe(() => this.newPasswordRepeat.updateValueAndValidity());
  }
}
